import { useMemo } from 'react'
import { usePageInfo, useRaidPlans } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import RaidPlan, {
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import {
  resolveEventActivityCastTargets,
  resolveEventActivityTimelineActors,
  resolveEventActivityTimelineAssignments,
  ResolvedTimelineActor,
  ResolvedAssignmentParticipant,
  toResolvedParticipants,
} from '../components/eventActivityTimelineResolver'
import {
  resolveLinkedPlanEncounterAssignmentRows,
  resolveLinkedPlanPhaseDividers,
} from './resolveLinkedPlanEncounterAssignmentRows'
import {
  EventPlanOverridesByPlanId,
  getActorOverrideCharacterId,
  getActorOverrideIgnored,
  getActorOverrideReplacementActor,
  getPlanActorOverrides,
  getPlanCastTargetOverrides,
  getPlanEncounterAssignmentOverrides,
} from '../components/eventActivityPlanOverrides'
import {
  applyEventActivityPlanOverrides,
  getEventActivityUnresolvedIssues,
} from './applyEventActivityPlanOverrides'

type LinkedPlanResolverResult = ReturnType<
  typeof resolveEventActivityTimelineAssignments
>
type LinkedPlanActorResolverResult = ReturnType<
  typeof resolveEventActivityTimelineActors
>
type LinkedPlanCastTargetResolverResult = ReturnType<
  typeof resolveEventActivityCastTargets
>

export type EventActivityResolvedAssignmentAbility = {
  abilityKey: string
  spellId: number
  name: string
}

export type EventActivityLinkedPlanAssignment = {
  abilityKey: string
  ability: RaidPlanEncounterAssignmentAbility
}

export type EventActivityLinkedPlanData = {
  linkedRaidPlan?: RaidPlan
  phaseDividers: { name: string; at: number }[]
  linkedPlanAssignments: EventActivityLinkedPlanAssignment[]
  selectedRows: EncounterAbilityRow[]
  selectedParticipants: ResolvedAssignmentParticipant[]
  resolvedAssignmentAbilitiesByCharacterId: Record<
    number,
    EventActivityResolvedAssignmentAbility[]
  >
  resolvedActors: ResolvedTimelineActor[]
  resolverResult: LinkedPlanResolverResult
  actorResolverResult: LinkedPlanActorResolverResult
  castTargetResolverResult: LinkedPlanCastTargetResolverResult
  linkedPlanIssueCount: number
  hasLinkedPlanIssues: boolean
}

const emptyResolverResult: LinkedPlanResolverResult = {
  resolvedByScopeBoxKey: {},
  issues: [],
}
const emptyActorResolverResult: LinkedPlanActorResolverResult = {
  resolvedActors: [],
  issues: [],
}
const emptyCastTargetResolverResult: LinkedPlanCastTargetResolverResult = {
  resolvedByCastId: {},
  issues: [],
}

const emptyLinkedPlanData: EventActivityLinkedPlanData = {
  phaseDividers: [],
  linkedPlanAssignments: [],
  selectedRows: [],
  selectedParticipants: [],
  resolvedAssignmentAbilitiesByCharacterId: {},
  resolvedActors: [],
  resolverResult: emptyResolverResult,
  actorResolverResult: emptyActorResolverResult,
  castTargetResolverResult: emptyCastTargetResolverResult,
  linkedPlanIssueCount: 0,
  hasLinkedPlanIssues: false,
}

type UseEventActivityLinkedPlanDataOptions = {
  activities: wowaudit.EventActivity[]
  enabled?: boolean
}

const useEventActivityLinkedPlanData = ({
  activities,
  enabled = true,
}: UseEventActivityLinkedPlanDataOptions) => {
  const { team } = useTeam()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const { allEntries } = useTeamEventParticipantsContext()
  const { data: raidPlans, isLoading: isRaidPlansLoading } = useRaidPlans(
    { ownerType: 'team', ownerId: team.id },
    enabled ? currentSeason?.id : undefined,
  )

  const linkedPlanDataByActivityId = useMemo(() => {
    const map = new Map<number, EventActivityLinkedPlanData>()
    const plansById = new Map((raidPlans ?? []).map((plan) => [plan.id, plan]))

    activities.forEach((activity) => {
      if (!activity.raidPlanId) {
        map.set(activity.id, emptyLinkedPlanData)
        return
      }

      const linkedRaidPlan = plansById.get(activity.raidPlanId)
      if (!linkedRaidPlan) {
        map.set(activity.id, emptyLinkedPlanData)
        return
      }

      const encounterRows = resolveLinkedPlanEncounterAssignmentRows({
        raidPlan: linkedRaidPlan,
        timeline: linkedRaidPlan.timeline,
        unknownAbilityLabel: 'Unknown ability',
      })
      const phaseDividers = resolveLinkedPlanPhaseDividers({
        raidPlan: linkedRaidPlan,
        timeline: linkedRaidPlan.timeline,
      })
      const abilitiesByKey =
        linkedRaidPlan.timeline.encounter_assignments?.abilities
      const abilityOrder =
        linkedRaidPlan.timeline.encounter_assignments?.ability_order
      const orderedAssignments = (abilityOrder ?? []).flatMap((abilityKey) => {
        const ability = abilitiesByKey?.[abilityKey]
        if (!ability) return []
        return [{ abilityKey, ability }]
      })
      const orderedKeySet = new Set(
        orderedAssignments.map((entry) => entry.abilityKey),
      )
      const missingAssignments = Object.entries(abilitiesByKey ?? {})
        .filter(([abilityKey]) => !orderedKeySet.has(abilityKey))
        .map(([abilityKey, ability]) => ({
          abilityKey,
          ability,
        }))
      const linkedPlanAssignments = [
        ...orderedAssignments,
        ...missingAssignments,
      ]
      const encounterRowByAbilityKey = encounterRows.reduce<
        Record<string, EncounterAbilityRow>
      >((acc, row) => {
        acc[row.abilityKey] = row
        return acc
      }, {})
      const selectedRows = linkedPlanAssignments
        .map((entry) => encounterRowByAbilityKey[entry.abilityKey])
        .filter((row): row is EncounterAbilityRow => Boolean(row))
      const resolverResult = resolveEventActivityTimelineAssignments({
        activityId: activity.id,
        entries: allEntries,
        encounterRows: selectedRows,
        abilities: linkedPlanAssignments.map((entry) => entry.ability),
      })
      const selectedParticipants = toResolvedParticipants(
        allEntries,
        activity.id,
      )
      const actorOverrides = getPlanActorOverrides(
        activity.planOverrides as EventPlanOverridesByPlanId,
        linkedRaidPlan.id,
      )
      const effectiveActors = (linkedRaidPlan.timeline.actors ?? []).map(
        (actor) => {
          const replacementActor = getActorOverrideReplacementActor({
            actorOverrides,
            actorUuid: actor.uuid,
          })
          return replacementActor ?? actor
        },
      )
      const actorResolverResult = resolveEventActivityTimelineActors({
        activityId: activity.id,
        entries: allEntries,
        actors: effectiveActors,
      })
      const resolvedActors = actorResolverResult.resolvedActors.reduce<
        ResolvedTimelineActor[]
      >((acc, resolvedActor) => {
        const isIgnored = getActorOverrideIgnored({
          actorOverrides,
          actorUuid: resolvedActor.actor.uuid,
        })
        const replacementActorOverride = getActorOverrideReplacementActor({
          actorOverrides,
          actorUuid: resolvedActor.actor.uuid,
        })
        const replacementPreferredCharacterId =
          replacementActorOverride?.preferred_character_id
        if (typeof replacementPreferredCharacterId === 'number') {
          const replacementParticipant = selectedParticipants.find(
            (participant) =>
              participant.characterId === replacementPreferredCharacterId,
          )
          if (replacementParticipant) {
            acc.push({
              ...resolvedActor,
              participant: replacementParticipant,
              issue: undefined,
              isOverridden: true,
              isIgnored,
            })
            return acc
          }
        }
        const currentParticipant = resolvedActor.participant
        const overrideCharacterId = getActorOverrideCharacterId({
          actorOverrides,
          actorUuid: resolvedActor.actor.uuid,
        })
        const previousCharacterIds = new Set(
          acc
            .map((entry) => entry.participant?.characterId)
            .filter(
              (characterId): characterId is number =>
                typeof characterId === 'number',
            ),
        )
        const overrideParticipant =
          typeof overrideCharacterId === 'number'
            ? selectedParticipants.find(
                (participant) =>
                  participant.characterId === overrideCharacterId,
              )
            : undefined
        const isOverrideParticipantEligible =
          overrideParticipant &&
          (resolvedActor.actor.spec_id !== null
            ? overrideParticipant.specId === resolvedActor.actor.spec_id
            : overrideParticipant.classId === resolvedActor.actor.class_id)
        const canUseOverrideParticipant =
          overrideParticipant &&
          isOverrideParticipantEligible &&
          (!previousCharacterIds.has(overrideParticipant.characterId) ||
            overrideParticipant.characterId === currentParticipant?.characterId)
        const isOverridden =
          canUseOverrideParticipant &&
          overrideParticipant.characterId !== currentParticipant?.characterId
        const participant = canUseOverrideParticipant
          ? overrideParticipant
          : currentParticipant
        const nextResolvedActor: ResolvedTimelineActor = participant
          ? {
              ...resolvedActor,
              participant,
              issue: undefined,
              isOverridden: isOverridden ? true : resolvedActor.isOverridden,
              isIgnored,
            }
          : {
              ...resolvedActor,
              isIgnored,
            }
        acc.push(nextResolvedActor)
        return acc
      }, [])
      const resolvedActorsWithCasts = resolvedActors.filter(
        (resolvedActor) => resolvedActor.actor.casts.length > 0,
      )
      const actorIssues = resolvedActorsWithCasts
        .filter(
          (resolvedActor) =>
            !resolvedActor.participant && resolvedActor.isIgnored !== true,
        )
        .map((resolvedActor) => {
          if (resolvedActor.issue) return resolvedActor.issue
          const available =
            resolvedActor.actor.spec_id !== null
              ? selectedParticipants.filter(
                  (participant) =>
                    participant.specId === resolvedActor.actor.spec_id,
                ).length
              : selectedParticipants.filter(
                  (participant) =>
                    participant.classId === resolvedActor.actor.class_id,
                ).length
          return {
            type: 'unmatched_actor' as const,
            actorLabel: resolvedActor.actor.label,
            requested: 1,
            available,
            missing: 1,
          }
        })
      const actorResolverResultWithOverrides = {
        ...actorResolverResult,
        resolvedActors: resolvedActorsWithCasts,
        issues: actorIssues,
      }
      const castTargetOverrides = getPlanCastTargetOverrides(
        activity.planOverrides as EventPlanOverridesByPlanId,
        linkedRaidPlan.id,
      )
      const castTargetResolverResult = resolveEventActivityCastTargets({
        activityId: activity.id,
        entries: allEntries,
        actors: resolvedActorsWithCasts.map(
          (resolvedActor) => resolvedActor.actor,
        ),
        generalCasts: linkedRaidPlan.timeline.general?.casts ?? [],
        castTargetOverrides,
      })
      const encounterAssignmentOverrides = getPlanEncounterAssignmentOverrides(
        activity.planOverrides as EventPlanOverridesByPlanId,
        linkedRaidPlan.id,
      )
      const resolvedByScopeBoxKey = applyEventActivityPlanOverrides({
        resolvedByScopeBoxKey: resolverResult.resolvedByScopeBoxKey,
        abilities: linkedPlanAssignments.map((entry) => entry.ability),
        encounterRows: selectedRows,
        selectedParticipants,
        encounterAssignmentOverrides,
      })
      const unresolvedIssues = getEventActivityUnresolvedIssues(
        resolvedByScopeBoxKey,
      )
      const resolverResultWithOverrides = {
        ...resolverResult,
        resolvedByScopeBoxKey,
        issues: unresolvedIssues,
      }
      const rowByAbilityKey = selectedRows.reduce<
        Record<string, EncounterAbilityRow>
      >((acc, row) => {
        acc[row.abilityKey] = row
        return acc
      }, {})
      const resolvedAbilityKeysByCharacterId: Record<number, Set<string>> = {}
      Object.entries(resolvedByScopeBoxKey).forEach(
        ([scopeBoxKey, entities]) => {
          const abilityKey = scopeBoxKey.split(':')[0]
          entities.forEach((entity) => {
            if (entity.kind !== 'participant') return
            if (
              !resolvedAbilityKeysByCharacterId[entity.participant.characterId]
            ) {
              resolvedAbilityKeysByCharacterId[entity.participant.characterId] =
                new Set<string>()
            }
            resolvedAbilityKeysByCharacterId[
              entity.participant.characterId
            ].add(abilityKey)
          })
        },
      )
      const resolvedAssignmentAbilitiesByCharacterId = Object.entries(
        resolvedAbilityKeysByCharacterId,
      ).reduce<Record<number, EventActivityResolvedAssignmentAbility[]>>(
        (acc, [characterIdString, abilityKeys]) => {
          const characterId = Number(characterIdString)
          acc[characterId] = Array.from(abilityKeys)
            .map((abilityKey) => {
              const row = rowByAbilityKey[abilityKey]
              if (!row) return null
              return {
                abilityKey,
                spellId: row.spellId,
                name: row.name,
              }
            })
            .filter(
              (ability): ability is EventActivityResolvedAssignmentAbility =>
                ability !== null,
            )
            .sort((left, right) => {
              if (left.name !== right.name) {
                return left.name.localeCompare(right.name)
              }
              return left.abilityKey.localeCompare(right.abilityKey)
            })
          return acc
        },
        {},
      )
      const linkedPlanIssueCount =
        unresolvedIssues.length +
        actorResolverResultWithOverrides.issues.length +
        castTargetResolverResult.issues.length

      map.set(activity.id, {
        linkedRaidPlan,
        phaseDividers,
        linkedPlanAssignments,
        selectedRows,
        selectedParticipants,
        resolvedAssignmentAbilitiesByCharacterId,
        resolvedActors: actorResolverResultWithOverrides.resolvedActors,
        resolverResult: resolverResultWithOverrides,
        actorResolverResult: actorResolverResultWithOverrides,
        castTargetResolverResult,
        linkedPlanIssueCount,
        hasLinkedPlanIssues: linkedPlanIssueCount > 0,
      })
    })

    return map
  }, [activities, allEntries, raidPlans])

  return {
    linkedPlanDataByActivityId,
    isRaidPlansLoading,
  }
}

export default useEventActivityLinkedPlanData
